import React, { useMemo } from 'react';
import { VehiculeConfig, Service, PersonnelConfig, Competence, FonctionArmement } from '../../../../../../shared/types/config';
import VehiculeGradePriority from './VehiculeGradePriority';

interface VehiculeModalProps {
  editingVehicule: VehiculeConfig | null;
  vehiculeForm: Omit<VehiculeConfig, 'id'>;
  setVehiculeForm: React.Dispatch<React.SetStateAction<Omit<VehiculeConfig, 'id'>>>;
  services: Service[];
  personnelConfigs: PersonnelConfig[];
  competences: Competence[];
  fonctionsArmement: FonctionArmement[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const inputCls = 'w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5';

const VehiculeModal: React.FC<VehiculeModalProps> = ({
  editingVehicule,
  vehiculeForm,
  setVehiculeForm,
  services,
  personnelConfigs,
  competences,
  fonctionsArmement,
  onClose,
  onSave,
}) => {
  const primaryFunctionCompetences = useMemo(() => {
    const f = fonctionsArmement.find((x) => x.id === vehiculeForm.primary_armament_function_id);
    const ids = (f?.id_competences || [])
      .map((item: any) => parseInt(String(item?.id ?? 0), 10))
      .filter((id) => Number.isInteger(id) && id > 0);
    return Array.from(new Set(ids))
      .map((id) => competences.find((c) => c.id === id))
      .filter((c): c is Competence => Boolean(c));
  }, [fonctionsArmement, vehiculeForm.primary_armament_function_id, competences]);

  const secondaryFunctionIds = vehiculeForm.secondary_armament_function_ids ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 rounded-xl border border-[#243052]/90 bg-[#0a1128] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1e2d4d]">
          <h2 className="text-base font-bold text-white">
            {editingVehicule ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1.5 hover:bg-[#1e2d4d] rounded-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-130px)]">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Nom *</label>
              <input
                type="text"
                value={vehiculeForm.nom}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, nom: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Type *</label>
              <input
                type="text"
                value={vehiculeForm.type}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, type: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Service *</label>
              <select
                value={Number(vehiculeForm.id_service) > 0 ? Number(vehiculeForm.id_service) : 0}
                onChange={(e) => {
                  const sid = parseInt(e.target.value, 10) || 0;
                  setVehiculeForm({ ...vehiculeForm, id_service: sid });
                }}
                className={inputCls}
                required
              >
                <option value={0}>Sélectionner un service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>{service.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Niveaux *</label>
              <input
                type="number"
                value={vehiculeForm.niveaux}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, niveaux: parseInt(e.target.value) || 1 })}
                className={inputCls}
                min="1"
                required
              />
            </div>
            <div>
              <label className={labelCls}>Prix *</label>
              <input
                type="number"
                value={vehiculeForm.prix}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, prix: parseFloat(e.target.value) || 0 })}
                className={inputCls}
                min="0"
                step="0.01"
                required
              />
            </div>
            <div>
              <label className={labelCls}>Kilométrage *</label>
              <input
                type="number"
                value={vehiculeForm.kilometrage}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, kilometrage: parseInt(e.target.value) || 0 })}
                className={inputCls}
                min="0"
                required
              />
            </div>
            <div>
              <label className={labelCls}>Fonction départ *</label>
              <input
                type="text"
                value={vehiculeForm.fonction_depart}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, fonction_depart: e.target.value })}
                className={inputCls}
                required
              />
            </div>
            <div>
              <label className={labelCls}>Capacité personnels min *</label>
              <input
                type="number"
                value={vehiculeForm.capacite_personnels_min}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_personnels_min: parseInt(e.target.value) || 0 })}
                className={inputCls}
                min="0"
                required
              />
            </div>
            <div>
              <label className={labelCls}>Capacité personnels max *</label>
              <input
                type="number"
                value={vehiculeForm.capacite_personnels_max}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_personnels_max: parseInt(e.target.value) || 0 })}
                className={inputCls}
                min="0"
                required
              />
            </div>
            <div>
              <label className={labelCls}>Capacité eau (L)</label>
              <input
                type="number"
                value={vehiculeForm.capacite_eau}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_eau: parseInt(e.target.value) || 0 })}
                className={inputCls}
                min="0"
              />
            </div>
            <div>
              <label className={labelCls}>Capacité émulseur (L)</label>
              <input
                type="number"
                value={vehiculeForm.capacite_emulseur}
                onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_emulseur: parseInt(e.target.value) || 0 })}
                className={inputCls}
                min="0"
              />
            </div>

            <div className="col-span-2">
              <label className={labelCls}>Fonction d'armement principale *</label>
              <select
                value={vehiculeForm.primary_armament_function_id ?? 0}
                onChange={(e) => {
                  const nextPrimaryId = parseInt(e.target.value, 10) || 0;
                  setVehiculeForm({
                    ...vehiculeForm,
                    primary_armament_function_id: nextPrimaryId > 0 ? nextPrimaryId : null,
                    fonction_depart:
                      nextPrimaryId > 0
                        ? (fonctionsArmement.find((f) => f.id === nextPrimaryId)?.nom || vehiculeForm.fonction_depart)
                        : vehiculeForm.fonction_depart,
                    secondary_armament_function_ids: secondaryFunctionIds.filter((id) => id !== nextPrimaryId),
                    id_fonctions_armement: secondaryFunctionIds.filter((id) => id !== nextPrimaryId),
                  });
                }}
                className={inputCls}
                required
              >
                <option value={0}>Sélectionner une fonction principale</option>
                {fonctionsArmement.map((f) => (
                  <option key={f.id} value={f.id}>{f.nom}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className={labelCls}>
                Fonctions d'armement secondaires
                {secondaryFunctionIds.length > 0 && (
                  <span className="ml-2 text-xs text-[#8ea0f5]">
                    ({secondaryFunctionIds.length} sélectionnée{secondaryFunctionIds.length > 1 ? 's' : ''})
                  </span>
                )}
              </label>
              <div className="rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 max-h-44 overflow-y-auto">
                {fonctionsArmement.length === 0 ? (
                  <p className="text-slate-400 text-sm">Aucune fonction disponible.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {fonctionsArmement
                      .filter((f) => f.id !== vehiculeForm.primary_armament_function_id)
                      .map((f) => {
                        const checked = secondaryFunctionIds.includes(f.id);
                        return (
                          <label key={f.id} className="flex items-start gap-2 p-2 rounded hover:bg-[#1e2d4d]/50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...secondaryFunctionIds, f.id]
                                  : secondaryFunctionIds.filter((id) => id !== f.id);
                                const uniq = Array.from(new Set(next));
                                setVehiculeForm({
                                  ...vehiculeForm,
                                  secondary_armament_function_ids: uniq,
                                  id_fonctions_armement: uniq,
                                });
                              }}
                              className="w-4 h-4 mt-0.5"
                            />
                            <span className="text-sm text-slate-300">{f.nom}</span>
                          </label>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-2">
              <label className={labelCls}>
                Compétences d&apos;armement (définies par la fonction principale)
                {primaryFunctionCompetences.length > 0 && (
                  <span className="ml-2 text-xs text-[#8ea0f5]">({primaryFunctionCompetences.length})</span>
                )}
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Les compétences ne se configurent plus directement sur le véhicule. Elles sont héritées de la fonction d&apos;armement principale.
              </p>
              {!vehiculeForm.primary_armament_function_id ? (
                <p className="text-slate-400 text-sm">Choisissez d&apos;abord une fonction principale.</p>
              ) : (
                <div className="rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 max-h-56 overflow-y-auto">
                  {primaryFunctionCompetences.length === 0 ? (
                    <p className="text-slate-400 text-sm">
                      Cette fonction principale n&apos;a aucune compétence configurée.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {primaryFunctionCompetences.map((c) => (
                        <div key={c.id} className="rounded border border-[#1e2d4d]/60 bg-[#0a132b]/60 px-2 py-1.5 text-sm text-slate-300">
                          {c.nom}
                          {c.is_specialite && (
                            <span className="ml-2 px-1.5 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded text-xs">Spé</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <VehiculeGradePriority
              vehiculeForm={vehiculeForm}
              setVehiculeForm={setVehiculeForm}
              personnelConfigs={personnelConfigs}
            />

            <div className="col-span-2 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Capacité victimes UR</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_victimes_ur ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_victimes_ur: parseInt(e.target.value, 10) || 0 })}
                    className={inputCls}
                    min="0"
                  />
                </div>
                <div>
                  <label className={labelCls}>Capacité victimes UA</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_victimes_ua ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_victimes_ua: parseInt(e.target.value, 10) || 0 })}
                    className={inputCls}
                    min="0"
                  />
                </div>
                <div>
                  <label className={labelCls}>Capacité impliqués</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_victimes_impliquees ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_victimes_impliquees: parseInt(e.target.value, 10) || 0 })}
                    className={inputCls}
                    min="0"
                  />
                </div>
                <div>
                  <label className={labelCls}>Capacité hostiles</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_hostiles ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_hostiles: parseInt(e.target.value, 10) || 0 })}
                    className={inputCls}
                    min="0"
                  />
                </div>
                <div>
                  <label className={labelCls}>Capacité véhicules accidentés</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_vehicules_accidentes ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_vehicules_accidentes: parseInt(e.target.value, 10) || 0 })}
                    className={inputCls}
                    min="0"
                  />
                </div>
                <div>
                  <label className={labelCls}>Capacité animaux</label>
                  <input
                    type="number"
                    value={vehiculeForm.capacite_animaux ?? 0}
                    onChange={(e) => setVehiculeForm({ ...vehiculeForm, capacite_animaux: parseInt(e.target.value, 10) || 0 })}
                    className={inputCls}
                    min="0"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vehiculeForm.specialiste}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, specialiste: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                Véhicule spécialiste (indicateur / règles métier côté jeu)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_remorque}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_remorque: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                Capable remorque
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_lot}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_lot: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                Capable lot
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vehiculeForm.capable_cellule}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_cellule: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                Capable cellule
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(vehiculeForm.capable_escorte_medicale)}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_escorte_medicale: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                Capable escorte médicale
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(vehiculeForm.capable_escorte_fdo)}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, capable_escorte_fdo: e.target.checked })}
                  className="w-4 h-4 rounded"
                />
                Capable escorte FDO
              </label>
              <label className="flex items-center gap-2 text-sm text-sky-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean((vehiculeForm as any).is_aerien)}
                  onChange={(e) => setVehiculeForm({ ...vehiculeForm, is_aerien: e.target.checked } as any)}
                  className="w-4 h-4 rounded accent-sky-500"
                />
                Voie aérienne (hélicoptère, drone…) — trajet vol d'oiseau sans facteur route
              </label>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#1e2d4d]">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors"
          >
            {editingVehicule ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehiculeModal;
